/**
 * The offers page — the only place in Emberreach where a link to an advertiser
 * appears.
 *
 * The game never navigates out. A player taps a sign, reads it, and keeps it;
 * this page is where they come back to it afterwards. That means every link
 * here is followed deliberately, by somebody not in the middle of playing.
 */

import { api, isSignedIn } from './api/client.js';
import type { SavedOfferView } from './api/gameTypes.js';

const listEl = document.getElementById('offers-list') as HTMLUListElement;
const statusEl = document.getElementById('offers-status') as HTMLParagraphElement;

function say(message: string): void {
  statusEl.textContent = message;
  statusEl.hidden = false;
  listEl.hidden = true;
}

/** "3 August 2026" — a date somebody reads, not one a machine sorts by. */
function readableDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    day: 'numeric', month: 'long', year: 'numeric'
  });
}

function render(offers: SavedOfferView[]): void {
  if (offers.length === 0) {
    say('You have not kept any offers yet. Tap a sign while you are playing and choose “Keep this offer”, and it will be waiting here.');
    return;
  }

  listEl.replaceChildren(...offers.map(card));
  listEl.hidden = false;
  statusEl.hidden = true;
}

function card(offer: SavedOfferView): HTMLLIElement {
  const item = document.createElement('li');
  item.className = offer.imageUrl === '' ? 'offer no-art' : 'offer';

  if (offer.imageUrl !== '') {
    const art = document.createElement('img');
    art.className = 'offer-art';
    art.src = offer.imageUrl;
    art.alt = `${offer.businessName} advertisement`;
    art.loading = 'lazy';
    item.append(art);
  }

  const body = document.createElement('div');
  body.className = 'offer-body';

  const name = document.createElement('h2');
  name.className = 'offer-name';
  name.textContent = offer.businessName;
  body.append(name);

  if (!offer.stillRunning) {
    const ended = document.createElement('span');
    ended.className = 'offer-ended';
    // Said before the offer text, so nobody reads the promise first.
    ended.textContent = 'This campaign has finished';
    body.append(ended);
  }

  if (offer.offerText !== '') {
    const text = document.createElement('p');
    text.className = 'offer-text';
    text.textContent = offer.offerText;
    body.append(text);
  }

  if (offer.promoCode !== '') {
    const code = document.createElement('p');
    code.className = 'offer-code';
    const label = document.createElement('span');
    label.textContent = 'Code ';
    const value = document.createElement('strong');
    value.textContent = offer.promoCode;
    code.append(label, value);
    body.append(code);
  }

  const meta = document.createElement('p');
  meta.className = 'offer-meta';
  meta.textContent = `Kept on ${readableDate(offer.savedAt)}`;
  body.append(meta);

  const actions = document.createElement('div');
  actions.className = 'offer-actions';

  if (offer.linkUnavailable || offer.destinationUrl === '') {
    /* The server refused to hand over the link. That happens when an advertiser
       changed their destination after approval to something that no longer
       passes the safety check — so there is deliberately no button here at all,
       rather than one that goes nowhere. */
    const note = document.createElement('p');
    note.className = 'offer-nolink';
    note.textContent = 'This advertiser has no working link at the moment. The code above still applies.';
    actions.append(note);
  } else {
    const open = document.createElement('a');
    open.className = 'offer-open';
    open.href = offer.destinationUrl;
    // A link off our site, opened in its own tab, with no referrer and no
    // ability to reach back into this page.
    open.target = '_blank';
    open.rel = 'noopener noreferrer external';
    open.textContent = `Visit ${offer.businessName}`;
    open.addEventListener('click', () => {
      // Recorded, not awaited. Nobody should wait on our bookkeeping to follow
      // a link they chose to follow.
      void api.openSavedOffer(offer.id).catch(() => undefined);
    });
    actions.append(open);
  }

  const remove = document.createElement('button');
  remove.type = 'button';
  remove.className = 'offer-remove';
  remove.textContent = 'Remove';
  remove.addEventListener('click', () => { void drop(offer, item, remove); });
  actions.append(remove);

  body.append(actions);
  item.append(body);
  return item;
}

async function drop(
  offer: SavedOfferView, item: HTMLLIElement, button: HTMLButtonElement
): Promise<void> {
  button.disabled = true;
  button.textContent = 'Removing…';
  try {
    await api.removeSavedOffer(offer.id);
    item.remove();
    if (listEl.children.length === 0) render([]);
  } catch {
    button.disabled = false;
    button.textContent = 'Remove';
    // What happened and what to do about it.
    button.insertAdjacentText('afterend', ' Could not remove that just now — try again.');
  }
}

async function load(): Promise<void> {
  // The refresh cookie survives leaving the game, so somebody who was playing
  // a minute ago arrives here already signed in.
  const user = await api.restore();
  if (user === null && !isSignedIn()) {
    say('Sign in to Emberreach to see the offers you kept.');
    return;
  }

  try {
    const { offers } = await api.savedOffers();
    render(offers);
  } catch {
    say('Could not load your offers just now. Refresh the page to try again.');
  }
}

void load();
